import { Component, NgZone } from "@angular/core";
import { combineLatest } from "rxjs";
import { filter, map, shareReplay, take } from "rxjs/operators";
import { RequestInvitationFormData, RequestInvitationModalData } from "../../../modals/request-invitation/request-invitation.model";
import { AcceptanceStatus, Client, deepReplace, UtilsService } from "projects/common/src/public-api";
import { ModalBehavior, ModalsService } from "projects/common/src/lib/services/modals.service";
import { AssessmentRestore, AssessmentsService, AssessmentUpdate } from "../../../services/assessments.service";
import { BusinessService } from "../../../services/business.service";
import { ActivatedRoute, Router } from "@angular/router";
import { UsersService } from "../../../services/users.service";
import { MatLegacySnackBar } from "@angular/material/legacy-snack-bar";
import { NotesService } from "../../../services/notes.service";
import { ProgressBarService } from "../../../services/progress-bar.service";
import { EstimatesService } from "../../../services/estimates.service";
import { InvoicesService } from "../../../services/invoices.service";
import { ScrollDispatcher } from "@angular/cdk/scrolling";
import { ScheduleService } from "../../../services/schedule.service";
import { ChatService } from "../../../services/chat.service";
import { ViewAsService } from "../../../services/view-as.service";
import { NavigationService } from "../../../services/navigation.service";
import { Assessment } from "../../../models/assessment.model";
import { ProgressBarStage } from "../../../models/progress-bar.model";
import { clientFromDocument, clientRestoreFromDocument } from "projects/common/src/lib/models/client-transform";
import { RequestInvitationModal } from "../../../modals/request-invitation/request-invitation.component";
import { sameRanges } from "projects/common/src/lib/models/time-range.model";
import { ConfirmationDialog } from "projects/common/src/lib/modals/confirmation-dialog/confirmation-dialog.component";
import { showSnackbar } from "projects/common/src/lib/components/snackbar/snackbar.component";
import { AssessmentOrJobComponent } from "./assessment-or-job.compenent";

@Component({
  selector: 'app-assessment-info',
  templateUrl: './assessment-or-job.component.html',
  styleUrls: ['../event-info.component.scss']
})
export class AssessmentComponent extends AssessmentOrJobComponent<Assessment> {
  submitStates = ['Submitted', 'Canceled'];

  assignees$ = combineLatest([
    this.usersService.users$,
    this.document$,
  ]).pipe(
    filter(([users, document]) => !!users && !!document),
    map(([users, document]) => {
      if (!users || !document)
        return [];
      const user = users.find((user) => user.id === (document.assigneeId ?? document.ownerId))!;
      const status: AcceptanceStatus = document.assigneeId === null 
        ? 'accepted' 
        : (document.denied ? 'denied' : 'pending');
      return [{user, acceptanceStatus: status as AcceptanceStatus}];
    }),
    shareReplay({bufferSize: 1, refCount: true})
  );

  ownerOrLastAssignedBy$ = combineLatest([this.document$, this.usersService.users$]).pipe(
    map(([document, users]) => {
      if (!document)
        throw new Error('Shouldn\'t be able to reach this since we check for nullability in this.document$');
      return users!.find(user => user.id === document.ownerId)!;
    }),
    shareReplay({bufferSize: 1, refCount: true})
  );
  
  constructor(
    private assessmentsService: AssessmentsService,
    modalsService: ModalsService,
    businessService: BusinessService,
    router: Router,
    route: ActivatedRoute,
    usersService: UsersService,
    snackbar: MatLegacySnackBar,
    notesService: NotesService,
    progressBarService: ProgressBarService,
    estimatesService: EstimatesService,
    invoicesService: InvoicesService,
    scrollDispatcher: ScrollDispatcher,
    viewAsService: ViewAsService,
    scheduleService: ScheduleService,
    chatService: ChatService,
    utilsService: UtilsService,
    navigationService: NavigationService,
    zone: NgZone,
  ) {
    super(
      progressBarService,
      estimatesService,
      invoicesService,
      chatService,
      snackbar,
      scheduleService,
      modalsService,
      businessService,
      router,
      route,
      usersService,
      notesService,
      scrollDispatcher,
      viewAsService,
      utilsService,
      navigationService,
      zone,
    );
    this.docSubject.next(this.assessmentsService.assessmentObservable(this.documentId, 'Assessment'));
  }

  getAssignees(document: Assessment): number[] {
    return [document.assigneeId ?? document.ownerId];
  }

  getSelectedStatusIndex(document: Assessment) {
    return this.submitStates.findIndex(status => status.toLowerCase() === document.status);
  }

  isLoopedIn(_userId: number) {
    return this.document$.pipe(
      map(document => {
        if(!document)
          return false;
        return !!document.assigneeId;
      })
    );
  }
  
  async deleteDocument(id: number, workflowId: number) {
    const url = this.router.url;
    const router = this.router;
    const notes = await this.notesService.notesObservable(workflowId).pipe(take(1)).toPromise();
    const assessment = await this.assessmentsService.getAssessment(id);
    const restoreData: AssessmentRestore = {
      inId: assessment.id,
      inCreatedAt: assessment.createdAt,
      inClient: await clientRestoreFromDocument(assessment),
      inOwnerId: assessment.ownerId,
      inRanges: assessment.ranges,
      inStatus: assessment.status,
      inDenied: assessment.denied,
      inWorkflowId: assessment.workflowId,
      inJobType: assessment.jobType,
      inNotes: notes!
    };
    if(assessment.assigneeId)
      restoreData.inAssignee = assessment.assigneeId;

    this.modalsService.open(ConfirmationDialog, {
      behavior: ModalBehavior.Dialog,
      disableClose: true,
      data: {
        title: "Delete",
        message: "Are you sure you want to delete this assessment?",
        actionTitle: "Delete",
        actionColor: 'warn',
        action: async () => {
          await this.assessmentsService.deleteAssessment(id);
          await this.close();
          showSnackbar(this.snackbar, {
            message: 'Assessment deleted',
            duration: 10000,
            actionText: 'Undo',
            action: async () => {
              await this.assessmentsService.restoreAssessment(restoreData);
              router.navigateByUrl(url);
            }
          });
        }
      }
    });
  }

  async createOrOpenJob(document: Assessment, jobStage: ProgressBarStage) {
    let jobId = jobStage.id;
    const workflowId = document.workflowId;
    if(jobStage.canOpen) {
      await this.router.navigate(['/jobs', workflowId, jobId]);
      return;
    }

    const data: RequestInvitationModalData = {
      initialData: {
        id: document.id,
        workflowId: document.workflowId,
        dialogType: document.docType,
        assignees: [],
        ranges: [],
      },
      changes: {
        jobType: document.jobType,
        dialogType: 'Job',
        client: deepReplace(clientFromDocument(document), null, ''),
        assignees: [],
        ranges: [],
      },
      onSubmit: ((data: Required<RequestInvitationFormData>) => this.onRequestInvitationSubmit(document, data, true)).bind(this),
    };
    this.modalsService.open(RequestInvitationModal, {
      behavior: ModalBehavior.Auto,
      breakpoint: RequestInvitationModal.BREAKPOINT,
      disableClose: true,
      data: data
    });
  }
    
  async resendClick() {
    const document = (await this.document$.pipe(take(1)).toPromise())!;

    // Check if client has an email address
    if (!document.email || document.email.trim() === '') {
      showSnackbar(this.snackbar, {
        message: 'Cannot send assessment: client email is required',
        duration: 3000
      });
      return;
    }

    this.assessmentsService.sendEmailForAssessment(document, false);
    showSnackbar(this.snackbar, {
      message: 'Sent by email'
    });
  }
  
  async submitStateChanged(value: string, document: Assessment) {
    await this.assessmentsService.updateAssessment({
      inId: document.id,
      inStatus: value.toLowerCase() as Assessment['status']
    });
  }
   
  async onRequestInvitationSubmit(original: Assessment, data: Required<RequestInvitationFormData>, create: boolean) {
    if(create) {
      await this.scheduleService.createDocument(data, original.workflowId);
      return null;
    }
    const currentUser = await this.currentUser$.pipe(take(1)).toPromise();
    const updateData: AssessmentUpdate = {
      inId: original.id,
      inClient: data.client as Client,
      inJobType: data.jobType,
      inRanges: data.ranges,
      inAssignee: data.assignees ? data.assignees[0].id : original.assigneeId
    };
    if(data.assignees) {
      updateData.inDenied = false;
      if(updateData.inAssignee === currentUser.id)
        updateData.inAssignee = null;
    }
    await this.assessmentsService.updateAssessment(updateData);
    const clientEmailChange = original.email !== data.client.email;
    const rangesChanged = !sameRanges(original.ranges, data.ranges);
    if(clientEmailChange || rangesChanged) {
      // Check if client has an email address before sending
      if (!data.client.email || data.client.email.trim() === '') {
        showSnackbar(this.snackbar, {
          message: 'Cannot send assessment: client email is required',
          duration: 3000
        });
      } else {
        await this.assessmentsService.sendEmailForFormData(data, original, !clientEmailChange);
      }
    }
    return null;
  }

}